import { animations, layouts, palette } from 'theme';
import { componentStyles } from 'utils/themeUtils';

export const styles = {
  desktopPage: {
    ...layouts.reportWrapper,
    height: '100%',
    minHeight: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: 2,
  },
  desktopGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) 300px',
    gap: 2,
    alignItems: 'start',
    minWidth: 0,
  },
  desktopMainColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  desktopWrapper: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100%',
    padding: 3,
  },
  mobilePlaceholder: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100%',
    padding: 3,
    textAlign: 'center',
    backgroundColor: palette.background.default,
    ...animations.appearanceEffect,
  },
  mobilePage: {
    ...layouts.reportWrapper,
    height: '100%',
    minHeight: '100%',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingBottom: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  mobileDaysList: {
    ...componentStyles.cardWrapperWithBorder,
    padding: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  mobileDayRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  mobileDayRowEditing: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    backgroundColor: 'rgba(46, 161, 248, 0.08)',
    borderRadius: 2,
    padding: '8px 10px',
    margin: '-8px -10px',
  },
  mobileDayHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  mobileDayActions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  mobileSlotList: {
    display: 'flex',
    flexDirection: 'column',
    paddingLeft: 1,
  },
  mobileSlotRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    paddingTop: 0.75,
    paddingBottom: 0.75,
  },
  mobileSlotDivider: {
    borderColor: palette.grey[200],
  },
  mobileSlotLabel: {
    minWidth: 24,
    fontWeight: 700,
    fontSize: 13,
    color: palette.primary.main,
  },
  mobileSlotSeparator: {
    fontSize: 16,
    color: palette.text.secondary,
    flexShrink: 0,
  },
  mobileTimeInput: {
    width: 120,
    '& .MuiInputBase-input': {
      fontSize: 13,
      padding: '6px 8px',
    },
  },
  mobileSlotDeleteButton: {
    color: palette.error.main,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1,
    marginLeft: 'auto',
    flexShrink: 0,
  },
  headerWrapper: {
    ...componentStyles.cardWrapperWithBorder,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: 2,
  },
  pageTitle: {
    ...componentStyles.graphHeader,
    whiteSpace: 'nowrap',
    lineHeight: 1.2,
  },
  controlsRow: {
    display: 'flex',
    width: '100%',
    alignItems: 'flex-end',
    gap: 3,
    flexWrap: 'wrap',
  },
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  },
  fieldLabel: {
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: palette.text.secondary,
  },
  applyChangeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    height: 38,
  },
  siteSelect: {
    width: 200,
    '& .MuiSelect-select': {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  applyModeGroup: {
    marginLeft: 0,
  },
  datePicker: {
    minWidth: 170,
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: palette.grey[300] },
      '&:hover fieldset': { borderColor: palette.primary.main },
      '&.Mui-focused fieldset': { borderColor: palette.primary.main },
    },
    '& .MuiInputBase-input': {
      color: palette.text.primary,
      fontSize: 13,
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: palette.primary.main,
      fontSize: 18,
    },
    '& .Mui-disabled .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: palette.grey[400],
    },
  },
  datePickerPopper: {
    '& .MuiPaper-root': {
      borderRadius: 2,
      boxShadow: '0 4px 24px rgba(46,161,248,0.15)',
      border: `1px solid ${palette.primary.light}`,
    },
    // Header: month/year label + navigation arrows
    '& .MuiPickersCalendarHeader-root': {
      backgroundColor: palette.primary.main,
      borderRadius: '8px 8px 0 0',
      margin: 0,
      padding: '8px 12px',
      color: '#fff',
    },
    '& .MuiPickersCalendarHeader-label': {
      color: '#fff',
      fontWeight: 600,
      fontSize: 14,
    },
    '& .MuiPickersArrowSwitcher-button': {
      color: '#fff',
      '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
    },
    '& .MuiDayCalendar-weekDayLabel': {
      color: palette.secondary.main,
      fontWeight: 600,
      fontSize: 11,
    },
    // Regular days
    '& .MuiPickersDay-root': {
      fontSize: 13,
      color: palette.text.primary,
      '&:hover': {
        backgroundColor: palette.primary.light,
        color: palette.primary.main,
      },
    },
    // Today (not selected)
    '& .MuiPickersDay-today:not(.Mui-selected)': {
      border: `2px solid ${palette.primary.main}`,
      color: palette.primary.main,
      fontWeight: 700,
    },
    // Selected day
    '& .MuiPickersDay-root.Mui-selected': {
      backgroundColor: `${palette.primary.main} !important`,
      color: '#fff !important',
      fontWeight: 700,
      '&:hover': { backgroundColor: `${palette.primary.dark} !important` },
      '&:focus': { backgroundColor: `${palette.primary.main} !important` },
    },
    // Disabled days (past)
    '& .MuiPickersDay-root.Mui-disabled': {
      color: palette.grey[300],
    },
    // Bottom action buttons (Today, Clear)
    '& .MuiDialogActions-root .MuiButton-root, & .MuiPickersLayout-actionBar .MuiButton-root': {
      color: palette.primary.main,
      fontWeight: 600,
      textTransform: 'none',
    },
  },
  systemFilterArea: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexWrap: 'wrap',
    height: 38,
  },
  systemGroupToggle: {
    boxSizing: 'border-box' as const,
    height: '36px',
    border: `1.5px solid ${palette.grey[300]}`,
    borderRadius: '9px',
    padding: '0 12px',
    minWidth: 'unset',
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    cursor: 'pointer',
    backgroundColor: '#ffffff',
    transition: 'all 0.12s',
    '& svg': {
      stroke: palette.grey[400],
      fill: palette.grey[400],
      display: 'block',
      width: 16,
      height: 16,
    },
    '&.selected': {
      backgroundColor: palette.primary.light,
      borderColor: palette.primary.main,
      boxShadow: `0 0 0 3px rgba(46, 161, 248, 0.2)`,
      '& svg': {
        stroke: palette.primary.dark,
        fill: palette.primary.dark,
      },
    },
    '&:hover:not(.selected)': {
      backgroundColor: palette.grey[100],
    },
  },
  systemGroupDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: palette.grey[300],
    flexShrink: 0,
    '.selected &': {
      backgroundColor: palette.primary.main,
    },
  },
  systemGroupLabel: {
    fontSize: '12.5px',
    fontWeight: 600,
    color: palette.text.secondary,
    '.selected &': {
      color: palette.primary.dark,
    },
  },
  systemFilterButtons: {
    display: 'flex',
    gap: 1,
  },
  daysListWrapper: {
    ...componentStyles.cardWrapperWithBorder,
    padding: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  weekHead: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    flexWrap: 'wrap',
  },
  weekHint: {
    fontSize: 12,
    color: palette.text.disabled,
  },
  mixedBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    backgroundColor: '#fff7e8',
    border: '1px solid #f2d79a',
    borderRadius: '9px',
    padding: '10px 12px',
    fontSize: 12.5,
    color: '#8a5a00',
  },
  mixedBannerIcon: {
    fontSize: 14,
    flexShrink: 0,
  },
  mixedBannerViewAs: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: 0.75,
    flexShrink: 0,
    color: '#8a5a00',
  },
  mixedBannerSelect: {
    fontSize: 12,
    color: '#8a5a00',
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#e6c583',
    },
    '& .MuiSelect-select': {
      padding: '4px 24px 4px 8px',
    },
  },
  daysList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  weekFoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    paddingTop: 1.5,
    marginTop: 0.5,
    borderTop: `1px solid ${palette.grey[200]}`,
  },
  footerActions: {
    display: 'flex',
    gap: 1,
  },
  dayDivider: {
    borderColor: palette.grey[300],
  },
  dayRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  dayRowEditing: {
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
    backgroundColor: 'rgba(46, 161, 248, 0.08)',
    borderRadius: 2,
    padding: '8px 10px',
    margin: '-8px -10px',
  },
  dayRowActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    flexShrink: 0,
  },
  saveButton: {
    backgroundColor: '#2EA1F8',
    color: '#ffffff',
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'none',
    borderRadius: '6px',
    '&:hover': { backgroundColor: '#1b8ee0' },
  },
  cancelButton: {
    color: '#354052',
    borderColor: '#d6e2f0',
    fontWeight: 600,
    fontSize: 13,
    textTransform: 'none',
    borderRadius: '6px',
    '&:hover': { borderColor: '#354052', backgroundColor: 'rgba(53,64,82,0.05)' },
  },
  dayHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 2,
  },
  dayLeftSection: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    minWidth: 0,
  },
  dayName: {
    minWidth: 120,
    fontWeight: 600,
    color: palette.text.primary,
  },
  dayNameDisabled: {
    minWidth: 120,
    fontWeight: 600,
    color: palette.text.secondary,
  },
  newTimeFrameButton: {
    textTransform: 'none',
    backgroundColor: palette.primary.light,
    color: palette.primary.dark,
    fontWeight: 700,
    border: `1px solid ${palette.primary.main}`,
    boxShadow: 'none',
    '&:hover': {
      backgroundColor: '#dcebfa',
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: palette.grey[400],
      backgroundColor: palette.grey[100],
      border: `1px solid ${palette.grey[200]}`,
      opacity: 1,
    },
  },
  timelineTrack: {
    position: 'relative',
    height: 38,
    borderRadius: 1,
    backgroundImage:
      'linear-gradient(#fbfcfe, #f4f7fb), repeating-linear-gradient(to right, transparent, transparent calc(100% / 8 - 1px), #e2e6ec calc(100% / 8 - 1px), #e2e6ec calc(100% / 8))',
    overflow: 'visible',
  },
  timelineTrackDisabled: {
    position: 'relative',
    height: 38,
    borderRadius: 1,
    background:
      'repeating-linear-gradient(45deg, #f3f5f8, #f3f5f8 8px, #eef1f5 8px, #eef1f5 16px)',
    overflow: 'hidden',
  },
  timelineEmptyHint: {
    position: 'absolute',
    left: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    fontSize: 11,
    color: palette.text.disabled,
  },
  carryIn: {
    position: 'absolute',
    top: 5,
    bottom: 5,
    left: 0,
    borderRadius: '6px',
    background:
      'repeating-linear-gradient(135deg, #e7e9fb, #e7e9fb 6px, #dfe1f7 6px, #dfe1f7 12px)',
    border: '1px dashed #9aa0d8',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0 7px',
    color: '#4a4f8a',
    fontSize: 10,
    fontWeight: 600,
    pointerEvents: 'none',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    zIndex: 1,
  },
  carryInLock: {
    fontSize: 9,
    opacity: 0.8,
  },
  reserveEdge: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 0,
    borderLeft: '2px dotted #9aa0d8',
    zIndex: 1,
    pointerEvents: 'none',
  },
  slotBar: {
    position: 'absolute',
    top: 3,
    height: 33,
    borderRadius: '6px',
    background: 'linear-gradient(180deg, #1c4f81, #123a63)',
    boxShadow: '0 1px 3px rgba(15, 49, 102, 0.4)',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 0,
    boxSizing: 'border-box',
    overflow: 'visible',
    '&:hover': {
      filter: 'brightness(1.08)',
    },
    '&:hover .slot-delete': {
      opacity: 1,
    },
  },
  slotBarOvernight: {
    background: 'linear-gradient(180deg, #2a2f66, #1a1f4a)',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: 0,
      top: 0,
      bottom: 0,
      width: '14px',
      background:
        'repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(255,255,255,0.18) 3px, rgba(255,255,255,0.18) 5px)',
      pointerEvents: 'none',
    },
  },
  slotBarDraft: {
    opacity: 0.55,
    outline: '1.5px dashed #1c4f81',
    outlineOffset: '1px',
  },
  slotMoonIcon: {
    marginRight: '4px',
    fontSize: 10,
    opacity: 0.95,
    flexShrink: 0,
  },
  slotHandle: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 8,
    backgroundColor: 'rgba(255,255,255,0.25)',
    cursor: 'ew-resize',
    zIndex: 2,
  },
  slotHandleLeft: {
    left: 0,
    borderRadius: '6px 0 0 6px',
    '&::after': {
      content: '""',
      position: 'absolute',
      left: '2px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '2px',
      height: '14px',
      backgroundColor: 'rgba(255,255,255,0.6)',
      borderRadius: '2px',
    },
  },
  slotHandleRight: {
    right: 0,
    borderRadius: '0 6px 6px 0',
    '&::after': {
      content: '""',
      position: 'absolute',
      right: '2px',
      top: '50%',
      transform: 'translateY(-50%)',
      width: '2px',
      height: '14px',
      backgroundColor: 'rgba(255,255,255,0.6)',
      borderRadius: '2px',
    },
  },
  slotDeleteButton: {
    position: 'absolute',
    top: -7,
    right: -7,
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: '#e8503a',
    color: '#ffffff',
    border: '1.5px solid #ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 9,
    lineHeight: 1,
    cursor: 'pointer',
    opacity: 0,
    transition: 'opacity 0.1s',
    zIndex: 3,
  },
  slotLabel: {
    flex: 1,
    margin: 0,
    padding: '0 10px',
    textAlign: 'center',
    fontSize: 12,
    fontWeight: 700,
    fontFamily: 'Rubik, sans-serif',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  hoursAxis: {
    display: 'flex',
    justifyContent: 'space-between',
    color: palette.text.disabled,
    fontSize: 9.5,
    lineHeight: 1.2,
    marginTop: 0.5,
  },
  hoursAxisLabel: {
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    '&:first-of-type': { textAlign: 'left' },
    '&:last-of-type': { textAlign: 'right' },
  },
  matrixWrapper: {
    ...componentStyles.cardWrapperWithBorder,
    padding: 1.5,
  },
  matrixHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 1,
  },
  matrixTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: palette.text.primary,
  },
  matrixInfoIcon: {
    fontSize: 18,
    color: palette.text.secondary,
  },
  matrixTable: {
    borderCollapse: 'separate',
    borderSpacing: '0 4px',
  },
  matrixHeadSite: {
    fontSize: 12,
    fontWeight: 600,
    color: palette.text.primary,
    borderBottom: `1px solid ${palette.grey[300]}`,
    padding: '6px 8px',
  },
  matrixHeadCell: {
    fontSize: 12,
    fontWeight: 600,
    color: palette.text.primary,
    borderBottom: `1px solid ${palette.grey[300]}`,
    padding: '6px 4px',
  },
  matrixSiteCell: {
    borderBottom: 0,
    padding: '6px 8px',
    backgroundColor: palette.primary.light,
    borderRadius: 1,
  },
  matrixSiteText: {
    fontSize: 12,
    fontWeight: 500,
    color: palette.text.primary,
    lineHeight: 1.2,
  },
  matrixCurrentMark: {
    fontSize: 10,
    color: palette.text.secondary,
    marginTop: 0.25,
  },
  matrixBodyCell: {
    borderBottom: 0,
    padding: 0,
    width: 56,
    height: 44,
    backgroundColor: palette.primary.light,
  },
  matrixCell: {
    width: 26,
    height: 26,
    borderRadius: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto',
  },
  matrixCellSet: {
    backgroundColor: '#e6f5e8',
    color: palette.success.main,
  },
  matrixCellUnset: {
    backgroundColor: palette.grey[100],
    color: palette.grey[300],
  },
  matrixSetIcon: {
    fontSize: 14,
    fontWeight: 700,
  },
  matrixUnsetIcon: {
    fontSize: 14,
    fontWeight: 700,
  },
  matrixLegend: {
    marginTop: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    flexWrap: 'wrap',
  },
  matrixLegendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 0.5,
  },
  matrixLegendText: {
    fontSize: 11,
    color: palette.text.secondary,
  },
  matrixFootnote: {
    fontSize: 11,
    color: palette.text.disabled,
    margin: '10px 2px 0',
  },
  holidaysCard: {
    ...componentStyles.cardWrapperWithBorder,
    padding: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 1,
  },
  holidaysHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
  },
  holidaysIcon: {
    width: 26,
    height: 26,
    borderRadius: '7px',
    backgroundColor: palette.primary.light,
    color: palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
  },
  holidaysTitle: {
    fontSize: 15,
    fontWeight: 600,
    color: palette.text.primary,
  },
  holidaysDescription: {
    fontSize: 12.5,
    color: palette.text.secondary,
    margin: '2px 0 4px',
  },
  holidaysManageButton: {
    alignSelf: 'flex-start',
    textTransform: 'none',
    backgroundColor: palette.primary.main,
    color: '#ffffff',
    fontWeight: 600,
    '&:hover': {
      backgroundColor: palette.primary.dark,
    },
  },
  holidaysSummary: {
    marginTop: 1.5,
    paddingTop: 1.5,
    borderTop: `1px solid ${palette.grey[200]}`,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.75,
  },
  holidaysSummaryRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontSize: 12.5,
    color: palette.text.primary,
  },
  holidaysSummaryCount: {
    backgroundColor: '#fdece8',
    color: '#c0392b',
    borderRadius: '6px',
    padding: '2px 8px',
    fontWeight: 700,
    fontSize: 12,
  },
  holidaysSummaryNext: {
    fontSize: 12,
    color: palette.text.secondary,
  },
  editorPopover: {
    padding: 2,
    width: 270,
    display: 'flex',
    flexDirection: 'column',
  },
  editorRow: {
    display: 'flex',
    gap: 1.25,
    marginBottom: 1.25,
  },
  editorField: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  editorLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: palette.text.disabled,
  },
  editorSelect: {
    width: '100%',
    fontSize: 13,
  },
  editorOvernightRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    fontSize: 12.5,
    color: palette.text.primary,
    marginBottom: 1.25,
  },
  editorDuration: {
    fontSize: 11,
    color: palette.text.secondary,
    marginBottom: 1.25,
  },
  editorApplyDaysLabel: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: palette.text.disabled,
    marginBottom: 0.75,
  },
  editorDayChips: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 0.6,
    marginBottom: 1.25,
  },
  editorDayChip: {
    border: `1px solid ${palette.grey[300]}`,
    backgroundColor: '#ffffff',
    borderRadius: '14px',
    padding: '3px 9px',
    fontSize: 11,
    fontWeight: 600,
    color: palette.text.secondary,
    cursor: 'pointer',
    '&.on': {
      borderColor: palette.primary.main,
      backgroundColor: palette.primary.light,
      color: palette.primary.dark,
    },
    '&.on.conflict': {
      borderColor: palette.warning.main,
      backgroundColor: '#fff7e8',
      color: '#8a5a00',
    },
  },
  editorDayChipShortcut: {
    border: `1px dashed ${palette.primary.main}`,
    backgroundColor: '#ffffff',
    borderRadius: '14px',
    padding: '3px 9px',
    fontSize: 11,
    fontWeight: 600,
    color: palette.primary.dark,
    cursor: 'pointer',
  },
  editorWarning: {
    fontSize: 11,
    color: '#c0392b',
    backgroundColor: '#fdece8',
    borderRadius: '6px',
    padding: '6px 8px',
    marginBottom: 1.25,
  },
  editorWarningAmber: {
    fontSize: 11,
    color: '#8a5a00',
    backgroundColor: '#fff7e8',
    border: '1px solid #f2d79a',
    borderRadius: '6px',
    padding: '6px 8px',
    marginBottom: 1.25,
  },
  editorActions: {
    display: 'flex',
    gap: 1,
    justifyContent: 'flex-end',
  },
  editorDeleteButton: {
    marginRight: 'auto',
    color: '#e8503a',
    fontWeight: 600,
    textTransform: 'none',
  },
  holidaysModalPaper: {
    width: 760,
    maxWidth: '100%',
    maxHeight: '90vh',
    borderRadius: '16px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 24px 60px rgba(16,32,56,.35)',
  },
  holidaysModalHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    background: 'linear-gradient(180deg, #1a82e0, #1478d4)',
    padding: '16px 20px',
  },
  holidaysModalHeaderIcon: {
    width: 34,
    height: 34,
    borderRadius: '9px',
    backgroundColor: 'rgba(255,255,255,0.16)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
    flexShrink: 0,
  },
  holidaysModalHeaderText: {
    flex: 1,
    minWidth: 0,
  },
  holidaysModalCloseButton: {
    color: '#ffffff',
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderRadius: '8px',
    width: 30,
    height: 30,
    flexShrink: 0,
    '&:hover': {
      backgroundColor: 'rgba(255,255,255,0.28)',
    },
  },
  holidaysModalTitle: {
    fontSize: 17,
    fontWeight: 700,
    color: '#ffffff',
    lineHeight: 1.2,
  },
  holidaysModalSubtitle: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.9)',
    marginTop: '1px',
  },
  holidaysModalBody: {
    display: 'grid',
    gridTemplateColumns: '1fr 260px',
    gap: 0,
    overflow: 'auto',
    flex: 1,
    minHeight: 0,
    '@media (max-width: 680px)': {
      gridTemplateColumns: '1fr',
    },
  },
  holidaysCalWrap: {
    padding: '18px 20px',
    borderRight: `1px solid ${palette.grey[100]}`,
  },
  holidaysCalNav: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  holidaysCalLabel: {
    fontWeight: 700,
    fontSize: 15,
    color: palette.text.primary,
  },
  holidaysCalGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '4px 2px',
  },
  holidaysCalDow: {
    textAlign: 'center',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: palette.text.disabled,
    padding: '4px 0',
  },
  holidaysCalCellPad: {
    aspectRatio: '1',
  },
  holidaysCalCell: {
    aspectRatio: '1',
    borderRadius: '9px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    color: palette.text.primary,
    cursor: 'pointer',
    userSelect: 'none',
    backgroundColor: '#fafbfd',
    '&:hover': {
      backgroundColor: '#eef4fb',
    },
  },
  holidaysCalCellWeekend: {
    color: palette.text.disabled,
  },
  holidaysCalCellToday: {
    outline: `2px solid ${palette.primary.main}`,
    outlineOffset: '-2px',
    fontWeight: 700,
  },
  holidaysCalCellHoliday: {
    backgroundColor: '#f6b9ab',
    color: '#7a1c0c',
    fontWeight: 700,
    '&:hover': {
      backgroundColor: '#f0a996',
    },
  },
  // Added this session, not yet saved to the server — amber instead of the saved days' red.
  holidaysCalCellHolidayPending: {
    backgroundColor: '#ffe1a3',
    color: '#7a4b00',
    fontWeight: 700,
    border: '1px dashed #cc8b00',
    '&:hover': {
      backgroundColor: '#ffd580',
    },
  },
  holidaysCalCellPreview: {
    backgroundColor: '#fbdcd3',
    color: '#7a1c0c',
  },
  holidaysCalHint: {
    fontSize: 11,
    color: palette.text.disabled,
    marginTop: '8px',
  },
  holidaysListPanel: {
    padding: '18px 16px',
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  holidaysListTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: palette.text.primary,
    marginBottom: '4px',
  },
  holidaysListHint: {
    fontSize: 11,
    color: palette.text.disabled,
    marginBottom: '12px',
  },
  holidaysListItems: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    flex: 1,
    overflowY: 'auto',
  },
  holidaysListSiteTitle: {
    fontSize: 12,
    fontWeight: 700,
    color: palette.text.secondary,
    marginTop: '4px',
  },
  holidaysListSiteEmpty: {
    fontSize: 12,
    color: palette.text.disabled,
  },
  holidaysItemSites: {
    fontSize: 11,
    color: palette.text.secondary,
  },
  holidaysListEmpty: {
    textAlign: 'center',
    color: palette.text.disabled,
    fontSize: 12,
    padding: '24px 8px',
  },
  holidaysItem: {
    border: `1px solid ${palette.grey[200]}`,
    borderRadius: '10px',
    padding: '8px 9px',
    backgroundColor: '#ffffff',
  },
  // Added this session, not yet saved — amber tint to match the calendar's pending cells.
  holidaysItemPending: {
    border: '1px dashed #cc8b00',
    backgroundColor: '#fff8ea',
  },
  holidaysItemPendingBadge: {
    fontSize: 10,
    fontWeight: 700,
    color: '#7a4b00',
    backgroundColor: '#ffe1a3',
    borderRadius: '6px',
    padding: '1px 6px',
    flexShrink: 0,
  },
  holidaysItemTop: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '8px',
  },
  holidaysItemDateGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: 0,
    overflow: 'hidden',
  },
  holidaysItemDate: {
    fontSize: 11.5,
    color: '#a53214',
    fontWeight: 700,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  holidaysItemSpan: {
    fontSize: 10,
    fontWeight: 700,
    color: '#a53214',
    backgroundColor: '#fbdcd3',
    borderRadius: '5px',
    padding: '1px 6px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  holidaysItemNameInput: {
    marginTop: '4px',
    '& .MuiInputBase-input': {
      fontSize: 13,
    },
  },
  holidaysItemDelete: {
    color: palette.text.disabled,
    '&:hover': {
      color: '#e8503a',
    },
  },
  holidaysItemRepeat: {
    marginLeft: 0,
    marginTop: '6px',
    '& .MuiFormControlLabel-label': {
      fontSize: 11,
      color: palette.text.secondary,
    },
  },
  holidaysModalFoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 2,
    borderTop: `1px solid ${palette.grey[100]}`,
    padding: '14px 20px',
  },
  holidaysModalNote: {
    fontSize: 11.5,
    color: palette.text.disabled,
  },
  holidaysConfirmDeleteBody: {
    padding: '20px 20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  holidaysConfirmDeleteTitle: {
    fontSize: 15,
    fontWeight: 700,
  },
  holidaysConfirmDeleteText: {
    fontSize: 13,
    color: palette.text.secondary,
  },
  sideColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    position: 'sticky',
    top: 0,
  },
  footerHint: {
    fontSize: 12,
    color: palette.text.secondary,
    padding: '0 4px',
  },
};
